import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  completeNewPassword,
  forgotPassword,
  signIn,
  startHostedUiFlow,
} from './cognito';
import { useAuth } from './AuthContext';
import { JotlyMark } from '../components/JotlyMark';

type Stage =
  | { kind: 'password' }
  | { kind: 'new-password'; session: string; userIdForSrp: string }
  | { kind: 'forgot-sent'; email: string };

type SocialProvider = 'SignInWithApple' | 'Google' | 'Facebook';

export function LoginScreen() {
  const navigate = useNavigate();
  const { setAuthed } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [stage, setStage] = useState<Stage>({ kind: 'password' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [socialBusy, setSocialBusy] = useState<SocialProvider | null>(null);

  const onSocialLogin = async (provider: SocialProvider) => {
    setError(null);
    setSocialBusy(provider);
    try {
      // Redirects to the Cognito Hosted UI; the return trip lands on
      // /auth/callback (CallbackScreen) which completes the token exchange.
      await startHostedUiFlow(provider);
    } catch (err) {
      setError((err as Error).message || 'Could not start social sign-in.');
      setSocialBusy(null);
    }
  };

  const anyBusy = busy || socialBusy !== null;

  const onPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await signIn(email, password);
      if (result.kind === 'ok') {
        setAuthed(true);
        navigate('/', { replace: true });
        return;
      }
      setStage({
        kind: 'new-password',
        session: result.session,
        userIdForSrp: result.userIdForSrp,
      });
    } catch (err) {
      setError((err as Error).message || 'Sign-in failed.');
    } finally {
      setBusy(false);
    }
  };

  const onNewPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (stage.kind !== 'new-password') return;
    setError(null);
    setBusy(true);
    try {
      await completeNewPassword({
        session: stage.session,
        userIdForSrp: stage.userIdForSrp,
        newPassword,
      });
      setAuthed(true);
      navigate('/', { replace: true });
    } catch (err) {
      setError((err as Error).message || 'Could not set new password.');
    } finally {
      setBusy(false);
    }
  };

  const onForgotPassword = async () => {
    setError(null);
    if (!email) {
      setError('Enter your email above, then tap "Forgot password?" again.');
      return;
    }
    setBusy(true);
    try {
      await forgotPassword(email);
      // PreventUserExistenceErrors=ENABLED means we always land here even for
      // unknown emails — safe to show a definitive "check your inbox" message.
      setStage({ kind: 'forgot-sent', email });
    } catch (err) {
      setError((err as Error).message || 'Could not send reset email.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
          <JotlyMark size={32} />
          <span style={{
            font: '700 18px/1 var(--rc-display)',
            letterSpacing: '-0.01em',
            color: 'var(--rc-ink)',
          }}>
            Jotly
          </span>
        </div>
        <h1 style={{
          font: '700 28px/1.1 var(--rc-display)',
          margin: '0 0 24px',
          letterSpacing: '-0.02em',
        }}>
          Welcome back.
        </h1>

        {stage.kind === 'password' && (
          <>
          {/* Apple visually first per Apple HIG when other social options are
              present — mirrors the mobile login screen. */}
          <div style={socialStackStyle}>
            <SocialButton
              label="Continue with Apple"
              background="#000"
              textColour="#fff"
              busy={socialBusy === 'SignInWithApple'}
              disabled={anyBusy}
              onClick={() => onSocialLogin('SignInWithApple')}
            />
            <SocialButton
              label="Continue with Google"
              background="#fff"
              textColour="#1f1f1f"
              borderColour="#dadce0"
              busy={socialBusy === 'Google'}
              disabled={anyBusy}
              onClick={() => onSocialLogin('Google')}
            />
            <SocialButton
              label="Continue with Facebook"
              background="#1877F2"
              textColour="#fff"
              busy={socialBusy === 'Facebook'}
              disabled={anyBusy}
              onClick={() => onSocialLogin('Facebook')}
            />
          </div>
          <div style={dividerStyle}>
            <span style={dividerLineStyle} />
            <span style={dividerTextStyle}>or with email</span>
            <span style={dividerLineStyle} />
          </div>
          <form onSubmit={onPasswordSubmit} style={formStyle}>
            <label style={labelStyle}>
              <span style={labelTextStyle}>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                style={inputStyle}
              />
            </label>
            <label style={labelStyle}>
              <span style={labelTextStyle}>Password</span>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                style={inputStyle}
              />
            </label>
            <button type="submit" disabled={anyBusy} style={primaryButtonStyle}>
              {busy ? 'Signing in…' : 'Sign in'}
            </button>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => navigate('/signup')}
                style={linkButtonStyle}
              >
                Create account
              </button>
              <button
                type="button"
                onClick={onForgotPassword}
                disabled={anyBusy}
                style={linkButtonStyle}
              >
                Forgot password?
              </button>
            </div>
          </form>
          </>
        )}

        {stage.kind === 'forgot-sent' && (
          <div>
            <p style={hintStyle}>
              If <strong style={{ color: 'var(--rc-ink)' }}>{stage.email}</strong> is
              registered, we've sent a reset link to it. Check your inbox (and
              spam folder) — the link opens a page to set a new password.
            </p>
            <button
              type="button"
              onClick={() => setStage({ kind: 'password' })}
              style={linkButtonStyle}
            >
              Back to sign in
            </button>
          </div>
        )}

        {stage.kind === 'new-password' && (
          <form onSubmit={onNewPasswordSubmit} style={formStyle}>
            <p style={hintStyle}>
              First time here — pick a new password to finish setup.
            </p>
            <label style={labelStyle}>
              <span style={labelTextStyle}>New password</span>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                minLength={8}
                style={inputStyle}
              />
            </label>
            <button type="submit" disabled={busy} style={primaryButtonStyle}>
              {busy ? 'Saving…' : 'Set password & continue'}
            </button>
          </form>
        )}

        {error && (
          <div role="alert" style={errorStyle}>{error}</div>
        )}
      </div>
    </div>
  );
}

interface SocialButtonProps {
  label: string;
  background: string;
  textColour: string;
  borderColour?: string;
  busy: boolean;
  disabled: boolean;
  onClick: () => void;
}

function SocialButton({
  label,
  background,
  textColour,
  borderColour,
  busy,
  disabled,
  onClick,
}: SocialButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        ...socialButtonStyle,
        background,
        color: textColour,
        border: borderColour ? `1px solid ${borderColour}` : '1px solid transparent',
        opacity: disabled && !busy ? 0.5 : 1,
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {busy ? 'Redirecting…' : label}
    </button>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};

const cardStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 420,
  background: 'var(--rc-card)',
  borderRadius: 18,
  padding: '34px 32px 28px',
  boxShadow: '0 12px 36px rgba(20,22,27,0.06), 0 1px 0 rgba(20,22,27,0.03)',
  border: '1px solid var(--rc-border)',
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const socialStackStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const socialButtonStyle: React.CSSProperties = {
  appearance: 'none',
  padding: '12px 16px',
  borderRadius: 12,
  font: '600 14px/1 var(--rc-sans)',
  textAlign: 'center',
};

const dividerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  margin: '18px 0',
};

const dividerLineStyle: React.CSSProperties = {
  flex: 1,
  height: 1,
  background: 'var(--rc-border)',
};

const dividerTextStyle: React.CSSProperties = {
  font: '500 11.5px/1 var(--rc-mono)',
  color: 'var(--rc-ink-2)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const labelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};

const labelTextStyle: React.CSSProperties = {
  font: '500 12.5px/1 var(--rc-mono)',
  color: 'var(--rc-ink-2)',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const inputStyle: React.CSSProperties = {
  padding: '12px 14px',
  borderRadius: 12,
  border: '1px solid var(--rc-border-2)',
  background: 'var(--rc-card-2)',
  font: '500 15px/1.2 var(--rc-sans)',
  color: 'var(--rc-ink)',
  outline: 'none',
};

const primaryButtonStyle: React.CSSProperties = {
  marginTop: 4,
  appearance: 'none',
  border: 0,
  cursor: 'pointer',
  padding: '12px 16px',
  borderRadius: 12,
  background: 'var(--rc-ink)',
  color: '#fff',
  font: '600 14px/1 var(--rc-sans)',
};

const linkButtonStyle: React.CSSProperties = {
  appearance: 'none',
  border: 0,
  background: 'transparent',
  cursor: 'pointer',
  padding: '8px 4px',
  color: 'var(--rc-ink-2)',
  font: '500 13px/1 var(--rc-sans)',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  alignSelf: 'center',
};

const errorStyle: React.CSSProperties = {
  marginTop: 12,
  padding: '10px 12px',
  borderRadius: 10,
  background: 'var(--rc-danger-soft)',
  color: 'var(--rc-danger)',
  font: '500 13px/1.4 var(--rc-sans)',
};

const hintStyle: React.CSSProperties = {
  margin: 0,
  font: '400 14px/1.5 var(--rc-sans)',
  color: 'var(--rc-ink-2)',
};

